import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaImage, FaSyncAlt, FaUpload } from 'react-icons/fa';

const MAX_FILE_SIZE_MB = 3;
const MAX_FILE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const IndstillingerKiosk = () => {
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDataUrl, setSelectedDataUrl] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const kioskUrl = useMemo(() => {
    if (typeof window === 'undefined') return '/kiosk';
    return `${window.location.origin}/kiosk`;
  }, []);

  const buildImageUrl = (value) => {
    if (!value) return '/api/indstillinger/kiosk/image';
    return `/api/indstillinger/kiosk/image?v=${encodeURIComponent(value)}`;
  };

  const formatUpdatedAt = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('da-DK');
  };

  const loadKioskConfig = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      if (!token) {
        throw new Error('Mangler login-token');
      }
      const res = await fetch('/api/indstillinger/kiosk', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error('Kunne ikke hente kiosk-indstillinger');
      }
      const data = await res.json();
      if (data?.has_image) {
        setImageUrl(data.image_url || buildImageUrl(data.updated_at));
      } else {
        setImageUrl('');
      }
      setUpdatedAt(data?.updated_at || '');
    } catch (err) {
      setError(err?.message || 'Kunne ikke hente kiosk-indstillinger');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadKioskConfig(true);
  }, [loadKioskConfig]);

  useEffect(() => {
    if (!selectedFile) {
      setSelectedDataUrl('');
    }
  }, [selectedFile]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Vælg venligst et billede');
      setMessage('');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setError(`Billedet er for stort (max ${MAX_FILE_SIZE_MB} MB)`);
      setMessage('');
      return;
    }
    setError('');
    setMessage('');
    setSelectedFile(file);
    setSelectedDataUrl('');
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        setError('Kunne ikke læse billedet');
        return;
      }
      setSelectedDataUrl(reader.result);
    };
    reader.onerror = () => {
      setError('Kunne ikke læse billedet');
      setSelectedFile(null);
    };
    reader.readAsDataURL(file);
  };

  const handlePickFile = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Vælg et billede først');
      return;
    }
    if (!selectedDataUrl) {
      setError('Kunne ikke læse billedet');
      return;
    }
    if (!token) {
      setError('Mangler login-token');
      return;
    }
    setUploading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/indstillinger/kiosk/image', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dataUrl: selectedDataUrl })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || 'Kunne ikke uploade billede');
      }
      setMessage('Kiosk-billede opdateret');
      setSelectedFile(null);
      setSelectedDataUrl('');
      const nextUpdatedAt = data?.updated_at || new Date().toISOString();
      setUpdatedAt(nextUpdatedAt);
      setImageUrl(data?.image_url || buildImageUrl(nextUpdatedAt));
    } catch (err) {
      setError(err?.message || 'Kunne ikke uploade billede');
    } finally {
      setUploading(false);
    }
  };

  const handleOpenKiosk = () => {
    if (typeof window === 'undefined') return;
    window.open('/kiosk', '_blank', 'noopener,noreferrer');
  };

  const handleRefresh = () => {
    loadKioskConfig(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-24 w-24 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  const activePreview = selectedDataUrl || imageUrl;

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Kiosk</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Upload et billede der vises i fuld skærm på kioskvisningen. Brug knappen til at åbne siden uden login.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={handleOpenKiosk}
            className="inline-flex items-center gap-2 bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors"
          >
            <FaExternalLinkAlt />
            Åbn kioskvisning
          </button>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Kiosk URL</label>
            <input
              type="text"
              value={kioskUrl}
              readOnly
              className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-2">
          <FaImage className="text-falck-blue" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Kiosk-billede</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Anbefalet opløsning: 1920 × 1080 eller højere. Billedet skaleres til fuld skærm.
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm">
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="aspect-video rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 overflow-hidden flex items-center justify-center">
              {activePreview ? (
                <img src={activePreview} alt="Kiosk preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center px-4">
                  Ingen kiosk-billede uploadet endnu.
                </div>
              )}
            </div>
            {updatedAt && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Senest opdateret: {formatUpdatedAt(updatedAt)}
              </p>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handlePickFile}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <FaUpload />
                Vælg billede
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-falck-blue text-white hover:bg-falck-dark-blue transition-colors disabled:opacity-60"
              >
                {uploading ? 'Uploader...' : 'Upload'}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <FaSyncAlt />
                Opdater
              </button>
            </div>

            {selectedFile && (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                Valgt fil: <span className="font-medium">{selectedFile.name}</span> (
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndstillingerKiosk;
